import { type Actor, UNRESTRICTED_ACTOR } from '@shared/core/policy';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

/**
 * THE WEB CLERK SEAM. One file.
 *
 *   TODAY   every consumer receives UNRESTRICTED_ACTOR, and every policy's
 *           `requires` map is empty, so the actor gate always passes.
 *
 *   LATER   read the session's permission claims here — e.g.
 *             const { currentUser } = useAuth()
 *             const actor = { permissions: currentUser?.permissions ?? [] }
 *           No consumer changes: they call usePolicy(), never useActor().
 *           This is also where the LaunchDarkly permissionsCan* deny-lists
 *           and the hardcoded Dawsons user id retire to (Follow-on ⑧–⑨).
 */
const ActorContext = createContext<Actor>(UNRESTRICTED_ACTOR);

export const PolicyProvider = ({ children, actor }: { children: ReactNode; actor?: Actor }) => {
  const value = useMemo(() => actor ?? UNRESTRICTED_ACTOR, [actor]);

  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
};

/**
 * Prefer `usePolicy()`. Reach for this only when building a new binding —
 * consumers that call `useActor` directly are re-creating the seam this
 * module exists to keep in one place.
 */
export const useActor = (): Actor => useContext(ActorContext);
